import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from './firebase';

/**
 * Service pour l'upload d'images vers Firebase Storage
 */
export class ImageUploadService {
  
  /**
   * Upload une seule image vers Firebase Storage
   * @param file - Le fichier image à uploader
   * @param path - Le chemin dans Firebase Storage (ex: 'posts', 'profiles', 'banners')
   * @returns Promise<string> - L'URL de téléchargement de l'image
   */
  static async uploadImage(file: File, path: string): Promise<string> {
    try {
      console.log(`🚀 Début upload image vers ${path}...`);
      
      // Validation du fichier
      if (!file.type.startsWith('image/')) {
        throw new Error('Le fichier doit être une image');
      }
      
      // Limite de taille (5MB)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        throw new Error('La taille de l\'image ne doit pas dépasser 5MB');
      }
      
      // Créer un nom de fichier unique
      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substring(2);
      const extension = file.name.split('.').pop();
      const fileName = `${timestamp}_${randomId}.${extension}`;
      
      console.log(`📁 Nom du fichier: ${fileName}`);
      
      // Référence vers le fichier dans Firebase Storage
      const storageRef = ref(storage, `${path}/${fileName}`);
      
      // Upload du fichier
      console.log(`⬆️ Upload en cours...`);
      const snapshot = await uploadBytes(storageRef, file);
      console.log(`✅ Image uploadée avec succès: ${path}/${fileName}`);
      
      // Récupérer l'URL de téléchargement
      const downloadURL = await getDownloadURL(snapshot.ref);
      console.log(`🔗 URL récupérée: ${downloadURL}`);
      
      return downloadURL;
    } catch (error) {
      console.error('❌ Erreur upload image:', error);
      throw error;
    }
  }

  /**
   * Upload multiple images pour un post
   * @param images - Array des fichiers images à uploader
   * @returns Promise<string[]> - Array des URLs de téléchargement
   */
  static async uploadPostImages(images: File[]): Promise<string[]> {
    try {
      if (images.length === 0) return [];
      
      // Limite de 4 images par post
      if (images.length > 4) {
        throw new Error('Maximum 4 images autorisées par post');
      }
      
      console.log(`🚀 Upload de ${images.length} image(s) pour un post...`);
      
      // Upload toutes les images en parallèle
      const uploadPromises = images.map(image => 
        this.uploadImage(image, 'posts')
      );
      
      const urls = await Promise.all(uploadPromises);
      
      console.log(`✅ ${urls.length} image(s) uploadée(s) avec succès`);
      return urls;
    } catch (error) {
      console.error('❌ Erreur upload images post:', error);
      throw error;
    }
  }

  /**
   * Upload image de profil
   * @param userId - ID de l'utilisateur
   * @param file - Le fichier image
   * @returns Promise<string> - L'URL de téléchargement
   */
  static async uploadProfilePicture(userId: string, file: File): Promise<string> {
    try {
      console.log(`🚀 Upload image de profil pour l'utilisateur ${userId}...`);
      const url = await this.uploadImage(file, `profiles/${userId}`);
      console.log(`✅ Image de profil uploadée avec succès`);
      return url;
    } catch (error) {
      console.error('❌ Erreur upload image de profil:', error);
      throw error;
    }
  }

  /**
   * Upload image de bannière
   * @param userId - ID de l'utilisateur
   * @param file - Le fichier image
   * @returns Promise<string> - L'URL de téléchargement
   */
  static async uploadBannerImage(userId: string, file: File): Promise<string> {
    try {
      console.log(`🚀 Upload bannière pour l'utilisateur ${userId}...`);
      const url = await this.uploadImage(file, `banners/${userId}`);
      console.log(`✅ Bannière uploadée avec succès`);
      return url;
    } catch (error) {
      console.error('❌ Erreur upload bannière:', error);
      throw error;
    }
  }

  /**
   * Valide un fichier image (type, taille, etc.)
   * @param file - Le fichier à valider
   * @returns Objet avec le statut de validation et message d'erreur
   */
  static validateImageFile(file: File): { isValid: boolean; error?: string } {
    // Vérifier si c'est une image
    if (!file.type.startsWith('image/')) {
      return { isValid: false, error: 'Le fichier doit être une image' };
    }

    // Vérifier la taille (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      return { isValid: false, error: 'La taille de l\'image ne doit pas dépasser 5MB' };
    }

    // Vérifier les formats acceptés
    const acceptedFormats = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!acceptedFormats.includes(file.type)) {
      return { isValid: false, error: 'Format non supporté. Utilisez JPG, PNG, GIF ou WebP' };
    }

    return { isValid: true };
  }
}
